/**
 * SuperFingersPageClient.tsx — Interactive sections of the Super Fingers sales page.
 *
 * Cycle 76-E: RSC conversion. Static sections extracted to _components/ RSC files.
 * This file now renders ONLY the interactive subtrees:
 *   §5  Author / Lineage — hover animation (useEffect + LightboxTrigger)
 *   §7  Pricing — currency toggle (useEffect + localStorage + DOM mutation)
 *   §7.5 Students Say — static testimonial cards + our review strip (reviewStrip prop)
 *   §8  FAQ — accordion (useState)
 *   §9  Final CTA — LightboxTrigger on book cover
 *
 * Sections migrated to RSC (_components/):
 *   §0 SFSlimHeader  — pure markup nav
 *   §1 SFHero        — hero + book cover image, no hooks
 *   §2 SFPain        — static pain statements
 *   §3 SFInside      — what's inside + the student video (our player)
 *   §4 SFWhyMethod   — method section + student-at-piano image
 *
 * Senja sunset (2026-09-26): no Senja scripts on this page any more.
 *
 * CSS: <style> block removed — css moved to sf8.css, imported by page.tsx.
 *
 * CSS scope: .sf8-* (isolated from .sf-*, .sf2-* through .sf7-*)
 * Brand kit: v1.41.
 * SOP citations: SOP-OPS-04, SOP-OPS-05, SOP-OPS-07, SOP-BRAND-01,
 *                SOP-WEBSITE-02, SOP-WEBSITE-03, SOP-OPS-06
 */

'use client';

import Image from 'next/image';
import { useState, useEffect, useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';
import { clearLegacyCurrency, writeCurrencyCookie } from '@/lib/currency-cookie';
import { SUPER_FINGERS_DISPLAY } from '@/lib/shop-prices';
import { LightboxProvider, LightboxTrigger } from './LightboxProvider';

// Speed pass 2026-09-28 (SOP-WEBSITE-03 Rule 4, bundle hygiene): the checkout
// pop-up is only ever rendered after "Get the Book" is pressed, but a static
// import pulled CheckoutModal — and with it Stripe.js (~850 KB, loadStripe runs
// at module load in components/checkout/shared/stripe-client.ts) — into every
// /super-fingers page load, where it competed with the hero image (mobile LCP
// 11–12 s in Lighthouse). The module now loads on buying intent (pointer or
// focus on the buy button) and at the latest on the click. The modal itself is
// unchanged.
const loadCheckoutModal = () => import('@/components/checkout/CheckoutModal');
const CheckoutModal = dynamic(loadCheckoutModal, { ssr: false, loading: () => null });

// ── Type definitions ──────────────────────────────────────────────────────────

interface Faq {
  question: string;
  answer: string;
}

interface SFContent {
  metaTitle: string;
  metaDescription: string;
  slimHeaderEyebrow: string;
  heroEyebrow: string;
  heroHeadline: string;
  heroStandfirst: string;
  heroScopeStrip: string;
  painEyebrow: string;
  painHeadline: string;
  painStatements: string[];
  insideEyebrow: string;
  insideHeadline: string;
  whyEyebrow: string;
  whyHeadline: string;
  whyBody: string;
  authorEyebrow: string;
  authorHeadline: string;
  pricingAnchor: string;
  pricingEyebrow: string;
  pricingHeadline: string;
  pricingCardHeading: string;
  pricingTierLabel: string;
  pricingIncludes: string[];
  pricingAddonLine: string;
  faqEyebrow: string;
  faqHeadline: string;
  faqs: Faq[];
  finalCtaEyebrow: string;
  finalCtaHeadline: string;
  finalCtaBody: string;
}

interface Props {
  content: SFContent;
  /** Resolved on the server by lib/shop-currency.ts (pf_currency_choice cookie, else the
   *  IP default) — the same decision /pro-member makes (2026-10-05). */
  initialCurrency: 'EUR' | 'USD';
  /** Senja sunset (2026-09-26): our review strip, server-rendered by page.tsx. */
  reviewStrip?: React.ReactNode;
}

// ── Inline SVG utility icons ──────────────────────────────────────────────────

const ChevronDownIcon = () => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 22 22"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 7L11 15L18 7" />
  </svg>
);

// Padlock icon — inline SVG per FB-9d1e6c (currentColor UI icon)
const LockIcon = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Conservatory badge SVG
const ConservatoryBadgeSVG = () => (
  <svg
    viewBox="0 0 40 40"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ color: 'var(--bg)', width: 36, height: 36 }}
  >
    <path d="M7 15 Q20 5 33 15" />
    <path d="M7 15 L33 15" />
    <path d="M8 18 Q20 17 32 18" />
    <path d="M11 19 Q10.5 24 11 30" />
    <path d="M14 19 Q13.5 24 14 30" />
    <path d="M19.5 19 Q19 24 19.5 30" />
    <path d="M22.5 19 Q23 24 22.5 30" />
    <path d="M28 19 Q27.5 24 28 30" />
    <path d="M31 19 Q31.5 24 31 30" />
    <path d="M6 30 Q20 29.5 34 30" />
    <path d="M8 33 Q20 32.5 32 33" />
    <path d="M28 33 Q30 35 29 37" />
    <path d="M31 33 Q32.5 35 32 37" />
    <path d="M29 37 L30.5 35.5 L32 37" />
  </svg>
);

// ── Avatar helper — renders photo if URL present, letter circle if not ────────
interface AvatarProps {
  initial: string;
  name: string;
  photoUrl?: string | null;
}

function TestimonialAvatar({ initial, name, photoUrl }: AvatarProps) {
  if (photoUrl) {
    return (
      <div className="sf8-students-say__card-avatar--photo" aria-hidden="true">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={photoUrl} alt={name} width={48} height={48} loading="lazy" decoding="async" />
      </div>
    );
  }
  return (
    <div className="sf8-students-say__card-avatar" aria-hidden="true">{initial}</div>
  );
}

// ── Lineage data (FROZEN from v6/v7) ─────────────────────────────────────────
const LINEAGE = [
  {
    img: '/images/about/arthur-rubinstein-standing-at-piano.webp',
    name: 'Arthur Rubinstein',
    years: '1887–1982',
    role: 'Pianist',
    objectPosition: 'center 25%',
  },
  {
    img: '/images/about/avi-schonfeld-solo.webp',
    name: 'Avi Schönfeld',
    years: undefined,
    role: 'Direct student of Rubinstein',
    objectPosition: 'center 30%',
  },
  {
    img: '/images/super-fingers/sf-lars-moscow.webp',
    name: 'Lars Nelissen',
    years: undefined,
    role: 'Direct student of Schönfeld',
    objectPosition: 'center 25%',
  },
];

// ── D9: Static local photos — for testimonial sources with no Senja ID ────────
const LOCAL_PHOTOS: Record<string, string> = {
  yvette: '/images/testimonials/yvette-vosbeek.webp',
};

// ── Photo URLs — Senja sunset 2026-09-26: Martin + Patricia load from our R2
// copies (media.pianofantasy.com), pixel-identical to the former cdn.senja.io files.
const SENJA_PHOTOS: Record<string, string | null> = {
  yvette:   LOCAL_PHOTOS.yvette,
  martin:   "https://media.pianofantasy.com/testimonials/avatars/93d0bb2b-6fbc-4494-9f5f-87c8a68f7ca1/e9ab9cb6-8d4a-43fb-a3b4-db26b9513f19.jpg",
  patricia: "https://media.pianofantasy.com/testimonials/avatars/6c6e19ca-ac13-4592-a135-f70bb5d42ad8/36567f8a-7ca3-44f1-9143-fef5ee5323c4.webp",
  jasmijn:  null,
  michael:  null,
};

// ── Main component ─────────────────────────────────────────────────────────────
// Cycle 76-E: This component now renders §5, §7, §7.5, §8, §9 only.
// §0-§4 are RSC sections imported in page.tsx.
// CSS is in sf8.css (imported by page.tsx).

export default function SuperFingersPageClient({
  content,
  initialCurrency,
  reviewStrip,
  // checkoutEur / checkoutUsd (GHL links) were retired on 2026-08-07 when the
  // Buy button became the Stripe pop-up cart (CheckoutModal, mode="one-time"). They were
  // kept on the interface "for back-compat" and that is exactly how the dead
  // URLs survived into the Product JSON-LD for eighteen days. Removed 2026-08-25.
}: Props) {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // ── One-time checkout pop-up cart (2026-08-07) ────────────────────────────
  // Replaces the GHL Buy link. Opens on the currency the page is showing right
  // now: the server-resolved initialCurrency, updated by every toggle click on
  // the page or in the pop-up (2026-10-05: no second, browser-side decision).
  const [checkoutCurrency, setCheckoutCurrency] = useState<'EUR' | 'USD' | null>(null);
  const shownCurrency = useRef<'EUR' | 'USD'>(initialCurrency);
  function openCheckout() {
    setCheckoutCurrency(shownCurrency.current);
  }

  // ── Currency application (imperative DOM) ─────────────────────────────────
  // Lifted out of the effect so the checkout modal's onCurrencyChange can re-run
  // it — a toggle INSIDE the modal updates the sales-page price row + CTA too.
  const applyCurrency = useCallback((currency: string) => {
    shownCurrency.current = currency === 'USD' ? 'USD' : 'EUR';
    document.querySelectorAll<HTMLButtonElement>('.sf8-pricing__toggle-btn').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.currency === currency));
      btn.classList.toggle('sf8-pricing__toggle-btn--active', btn.dataset.currency === currency);
    });
    document.querySelectorAll<HTMLElement>('.sf8-pricing [data-eur][data-usd]').forEach((el) => {
      const text = currency === 'EUR' ? el.dataset.eur : el.dataset.usd;
      if (text !== undefined) el.textContent = text;
    });
    document.querySelectorAll<HTMLAnchorElement>('.sf8-pricing .sf8-pricing-cta').forEach((el) => {
      const href = el.getAttribute(`data-${currency.toLowerCase()}-href`);
      if (href) el.href = href;
    });
  }, []);

  // Modal → page sync: a toggle inside the checkout modal writes the pf_currency_choice cookie,
  // re-prices the sales-page row via applyCurrency, and re-renders the modal
  // (setCheckoutCurrency) so the controlled modal reflects the new currency.
  const handleModalCurrencyChange = useCallback(
    (cur: 'EUR' | 'USD') => {
      writeCurrencyCookie(cur);
      applyCurrency(cur);
      setCheckoutCurrency(cur);
    },
    [applyCurrency]
  );

  // ── Currency toggle (FROZEN from v7, selectors updated to sf8-*) ──────────
  useEffect(() => {
    // 2026-09-14: currency memory is the pf_currency_choice COOKIE (2026-10-05), written only on a
    // toggle click. The retired localStorage key is ignored and cleared.
    // 2026-10-05: the page is already rendered in initialCurrency on the server
    // (cookie, else IP default — lib/shop-currency.ts), so load applies nothing.
    const DEFAULT_CURRENCY: string = initialCurrency;
    clearLegacyCurrency();

    function setStoredCurrency(c: string) {
      writeCurrencyCookie(c === 'USD' ? 'USD' : 'EUR');
    }

    const btns = Array.from(
      document.querySelectorAll<HTMLButtonElement>('.sf8-pricing__toggle-btn')
    );
    const handlers: Array<[HTMLButtonElement, () => void]> = [];
    btns.forEach((btn) => {
      const handler = () => {
        const currency = btn.dataset.currency || DEFAULT_CURRENCY;
        setStoredCurrency(currency);
        applyCurrency(currency);
      };
      btn.addEventListener('click', handler);
      handlers.push([btn, handler]);
    });
    return () => handlers.forEach(([btn, handler]) => btn.removeEventListener('click', handler));
  }, [initialCurrency, applyCurrency]);

  // ── Desktop hover scoped to photo-frame only ──────────────────────────────
  // Old: section-level mousemove + Y-band check fired is-hovering anywhere in section scroll.
  // New: mouseenter/mouseleave on the photo-frame element only.
  // Mobile (≤767px): hover events skipped via matchMedia; CSS also suppresses transitions.
  useEffect(() => {
    const photoFrame = document.querySelector<HTMLElement>('.sf8-author__photo-frame');
    const photoCont = document.querySelector<HTMLElement>('.sf8-author__photo-col');
    if (!photoFrame || !photoCont) return;

    // Desktop only (≥1024px — D4 moves 2-col to ≥1024px)
    const mqDesktop = window.matchMedia('(min-width: 1024px)');
    if (!mqDesktop.matches) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    function onEnter() {
      photoCont!.classList.add('is-hovering');
    }
    function onLeave() {
      photoCont!.classList.remove('is-hovering');
    }

    photoFrame.addEventListener('mouseenter', onEnter);
    photoFrame.addEventListener('mouseleave', onLeave);

    return () => {
      photoFrame.removeEventListener('mouseenter', onEnter);
      photoFrame.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  // ── D12 (carried from v7 D12): Mobile IntersectionObserver scroll-trigger ──
  useEffect(() => {
    const photo = document.querySelector<HTMLElement>('.sf8-author__photo-col');
    if (!photo) return;

    const mqMobile = window.matchMedia('(max-width: 767px)');
    if (!mqMobile.matches) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.6) {
            photo.classList.add('is-hovering');
          } else if (entry.intersectionRatio < 0.4) {
            photo.classList.remove('is-hovering');
          }
        });
      },
      { threshold: [0.0, 0.4, 0.6, 1.0] }
    );

    observer.observe(photo);
    return () => observer.disconnect();
  }, []);

  // Display strings from lib/shop-prices.ts — the same ones the /store listing uses.
  const { EUR: sfEur, USD: sfUsd } = SUPER_FINGERS_DISPLAY;
  const initialPrice = SUPER_FINGERS_DISPLAY[initialCurrency].price;
  const initialAnchor = SUPER_FINGERS_DISPLAY[initialCurrency].reference;

  return (
    // LightboxProvider wraps §5 Author + §9 Final CTA (both use LightboxTrigger).
    // §0–§4 RSC sections rendered by page.tsx are outside this provider —
    // they contain no Lightbox references. §5+ sections start here.
    <LightboxProvider>

      {/* ════ §5 AUTHOR + LINEAGE ═══════════════════════════════════════════════
          Cycle-9 Changes 3+4: JSX restructured — 4 direct children of sf8-author__inner:
            1. sf8-author__text-content (eyebrow+H2+body+pull-quote)
            2. sf8-author__lineage-col  (lineage portraits)
            3. sf8-author__photo-col    (photo composition — is-hovering attaches here)
            4. sf8-author__schofield-card (Schönfeld endorsement)
          Desktop: grid-template-areas preserves identical visual (photo+Schönfeld left, text+lineage right).
          Mobile order (Change 3): text-content(0) → lineage(1) → photo(2) → schofield(3).
          HALF A: desktop layout UNCHANGED — photo LEFT (grid-area:photo), text RIGHT. */}
      <section className="sf8-author" id="author-section" aria-labelledby="author-h2">
        <div className="sf8-author__inner">

          {/* ── TEXT CONTENT — grid-area:text (desktop RIGHT col, row 1) ──
              Mobile order:0 — appears first on mobile (intro text about Lars). */}
          <div className="sf8-author__text-content">
            {/* Slot 1: Eyebrow */}
            <div className="eyebrow eyebrow--on-cream sf8-author__eyebrow" style={{ marginBottom: 18 }}>
              {content.authorEyebrow}
            </div>
            {/* Slot 2: H2 */}
            <h2 id="author-h2" className="sf8-author__h2">
              {content.authorHeadline}
            </h2>
            {/* Slot 3: Italic lead — D6: color --eyebrow-on-cream; mobile 22px */}
            <p className="sf8-author__italic-lead">
              Three names, one chain — and a teacher who spent decades figuring out what to do with what he was given.
            </p>
            {/* Slot 4: Body */}
            <p className="sf8-author__body">
              Lars Nelissen studied for <strong>seven years</strong> with Avi Sch&ouml;nfeld &mdash; himself a direct student of Arthur Rubinstein. Twenty-five years of teaching since, most of them spent on a single technical problem: <em>how the fingers and the forearm actually coordinate at speed, and why training the fingers alone leaves a ceiling no amount of practice gets you past.</em> Super Fingers is what that work settled into &mdash; <em>sixty exercises, trained as one coordinated system.</em>
            </p>
            {/* Slot 5: Pull-quote */}
            <blockquote className="sf8-pull-quote--featured">
              The book is the result. Sixty <strong>fundamental</strong> exercises, and a way of practising them that finally treats finger independence and arm technique as one piece of work.
            </blockquote>
          </div>

          {/* ── LINEAGE — grid-area:lineage (desktop RIGHT col, row 2) ──
              Mobile order:1 — appears second on mobile (lineage chain). */}
          <nav className="sf8-author__lineage-col sf8-lineage" aria-label="Lars's pedagogical lineage">
            <div className="sf8-lineage__list" role="list">
              <div className="sf8-lineage__spine" aria-hidden="true" />
              {/* Cycle-10 Change 1: lineage portraits wrapped in LightboxTrigger */}
              {LINEAGE.map((person, i) => (
                <div key={i} className="sf8-lineage__item" role="listitem">
                  <LightboxTrigger
                    src={person.img}
                    alt={person.name + (person.years ? ` (${person.years})` : '') + ' — ' + person.role}
                  >
                    <div className="sf8-lineage__portrait" aria-hidden="true">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={person.img} alt="" width={76} height={76} loading="lazy" style={{ objectPosition: person.objectPosition }} />
                    </div>
                  </LightboxTrigger>
                  <div>
                    <span className="sf8-lineage__name">{person.name}</span>
                    {person.years && <span className="sf8-lineage__years">{person.years}</span>}
                    <span className="sf8-lineage__role">{person.role}</span>
                  </div>
                </div>
              ))}
            </div>
          </nav>

          {/* ── PHOTO COMPOSITION — grid-area:photo (desktop LEFT col, row 1) ──
              Mobile order:2 — appears third on mobile (Lars's photo).
              is-hovering class attaches to this element (Change 2: JS now fires mouseenter/leave
              on the photo-frame child, adds is-hovering here). */}
          {/* ── Cycle-10 Change 1: §5 main portrait wrapped in LightboxTrigger ── */}
          <figure className="sf8-author__photo-col" aria-label="Lars Nelissen at the piano">
            <div className="sf8-author__photo-frame">
              <div className="sf8-author__offset-block" aria-hidden="true" />
              <div className="sf8-author__accent-line sf8-author__accent-line--tl" aria-hidden="true" />
              <LightboxTrigger
                src="/images/super-fingers/sf-lars-moscow.webp"
                alt="Lars Nelissen in Moscow — piano teacher and author of Super Fingers"
              >
                <div className="sf8-author__portrait-wrap">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/images/super-fingers/sf-lars-moscow.webp"
                    alt="Lars Nelissen in Moscow — piano teacher and author of Super Fingers"
                    width={800} height={800}
                    loading="lazy" decoding="async"
                    className="sf8-author__portrait"
                  />
                  <div className="sf8-author__portrait-inner-border" aria-hidden="true" />
                </div>
              </LightboxTrigger>
              <div className="sf8-author__accent-line sf8-author__accent-line--br" aria-hidden="true" />
              <div className="sf8-author__badge" aria-label="Conservatory Certified">
                <ConservatoryBadgeSVG />
                <div className="sf8-author__badge-label">Conservatory<br />Certified</div>
              </div>
            </div>
          </figure>

          {/* ── SCHÖNFELD CARD — grid-area:schofield (desktop LEFT col, row 2) ──
              Mobile order:3 — appears fourth on mobile (endorsement after photo). */}
          <div className="sf8-author__schofield-card">
            <div className="eyebrow eyebrow--on-cream sf8-author__schofield-eyebrow">
              Endorsement from Lars&rsquo;s teacher
            </div>
            <figure aria-label="Endorsement of Lars Nelissen by his conservatory teacher">
              <blockquote className="sf8-schofield-quote">
                <p>&ldquo;Lars has been a student in my piano class at the conservatory for several years. I highly appreciate his musicality, artistic qualities and imagination. His creativity gives me the feeling he&rsquo;ll be an excellent teacher, therefore I recommend him warmly.&rdquo;</p>
              </blockquote>
              <figcaption className="sf8-authority-attribution">
                <span className="sf8-authority-name">Avi Sch&ouml;nfeld</span>
                <span className="sf8-authority-role">Piano Professor &amp; Composer</span>
              </figcaption>
            </figure>
          </div>

        </div>
      </section>

      {/* ════ §7 PRICING (FROZEN from v7, renamed sf8-*) ═════════════════════ */}
      <section className="sf8-pricing" id="pricing" aria-labelledby="pricing-h2">
        <div className="sf8-pricing__inner">
          <div className="eyebrow sf8-pricing__eyebrow sf8-eyebrow-bracketed">{content.pricingEyebrow}</div>
          <h2 id="pricing-h2" className="sf8-pricing__h2">{content.pricingHeadline}</h2>
          {/* Cycle-11 Change 3: tablet image is decorative-only — lightbox removed, blog-card hover scale */}
          <div className="sf8-pricing__tablet-img-wrap">
            <Image src="/images/super-fingers/sf-ebook-on-tablet.webp" alt="Super Fingers ebook displayed on a tablet — instant PDF download" width={810} height={608} sizes="(max-width: 767px) 320px, 480px" loading="lazy" decoding="async" className="sf8-pricing__tablet-image" />
          </div>
          <div className="sf8-pricing__card">
            <div className="sf8-pricing__card-title">{content.pricingCardHeading}</div>
            <div className="sf8-pricing__card-subtitle">{content.pricingTierLabel}</div>
            <div className="sf8-pricing__toggle" role="group" aria-label="Select currency">
              {(['EUR', 'USD'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`sf8-pricing__toggle-btn${initialCurrency === c ? ' sf8-pricing__toggle-btn--active' : ''}`}
                  data-currency={c}
                  aria-pressed={initialCurrency === c}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="sf8-pricing__card-body">
              <div className="sf8-pricing__left-col">
                <div className="sf8-pricing__includes-heading">What&rsquo;s included</div>
                <ul className="sf8-pricing__includes" role="list" aria-label="What's included with Super Fingers Book 1">
                  {content.pricingIncludes.map((item, i) => (
                    <li key={i} className="sf8-pricing__includes-item">{item}</li>
                  ))}
                </ul>
              </div>
              <div className="sf8-pricing__right-col">
                <div className="sf8-pricing__price-block">
                  <span className="sf8-pricing__live-price" data-eur={sfEur.price} data-usd={sfUsd.price}>{initialPrice}</span>
                  <span className="sf8-pricing__anchor-price" data-eur={sfEur.reference} data-usd={sfUsd.reference}>{initialAnchor}</span>
                </div>
                {/* Cycle-10 Change 3: arrow removed; push-up hover from .sf8-pricing-cta. */}
                <button type="button" onClick={openCheckout} onPointerEnter={loadCheckoutModal} onFocus={loadCheckoutModal} onTouchStart={loadCheckoutModal} className="btn btn-primary sf8-pricing-cta">
                  Get the Book
                </button>
              </div>
            </div>
            <div className="sf8-pricing__trust-line"><LockIcon /><span>Secure payment by Stripe</span></div>
            <p className="sf8-pricing__addon-line">{content.pricingAddonLine}</p>
          </div>
        </div>
      </section>

      {/* ════ §7.5 STUDENTS SAY (D9: Yvette photo; D10: Martin trim) ═══════════
          D9: Yvette Vosbeek photo wired via LOCAL_PHOTOS map → SENJA_PHOTOS.yvette
          D10: Martin Reuvers trimmed to 3 sentences ending "...continuously."
               Senja record 8035944d-61a2-484e-9465-265e8c75b1c4 unchanged (render-only truncation)
          Senja sunset 2026-09-26: the strip below the three cards is our own ReviewStrip. */}
      <section className="sf8-students-say" aria-label="What students say">
        <div className="sf8-students-say__inner">
          <div className="eyebrow eyebrow--on-cream" style={{ marginBottom: 'var(--space-32)' }}>
            What students say
          </div>
          <div className="sf8-students-say__grid">
            {/* Card 1: Yvette V. — D9: real photo wired via LOCAL_PHOTOS.yvette */}
            <div className="sf8-students-say__card">
              <blockquote className="sf8-students-say__card-quote">
                &ldquo;Due to Lars&rsquo; method of arm and finger technique, my piano play has improved a lot. He goes into great depth and that gives me a lot of joy in playing the piano. Amazing teacher!&rdquo;
              </blockquote>
              <div className="sf8-students-say__card-person">
                <TestimonialAvatar initial="Y" name="Yvette Vosbeek" photoUrl={SENJA_PHOTOS.yvette} />
                <div>
                  <div className="sf8-students-say__card-name">Yvette V.</div>
                  <div className="sf8-students-say__card-role">Piano Student</div>
                </div>
              </div>
            </div>
            {/* Card 2: Martin Reuvers — D10: trimmed to 3 sentences
                Senja ID: 8035944d-61a2-484e-9465-265e8c75b1c4 — record unchanged, render-only truncation */}
            <div className="sf8-students-say__card">
              <blockquote className="sf8-students-say__card-quote">
                &ldquo;I&rsquo;m a pro member of Pianofantasy for 1 year now. I already learned a lot from Lars&rsquo; Super Fingers course, the live lessons and feedback that I and other students get. It&rsquo;s very inspiring and motivating and it makes me want to improve my piano skills continuously.&rdquo;
              </blockquote>
              <div className="sf8-students-say__card-person">
                <TestimonialAvatar initial="M" name="Martin Reuvers" photoUrl={SENJA_PHOTOS.martin} />
                <div>
                  <div className="sf8-students-say__card-name">Martin Reuvers</div>
                  <div className="sf8-students-say__card-role">Piano Student</div>
                </div>
              </div>
            </div>
            {/* Card 3: Patricia Cole — FROZEN */}
            <div className="sf8-students-say__card">
              <blockquote className="sf8-students-say__card-quote">
                &ldquo;Becoming a member of Lars Piano Fantasy Academy has proven beneficial for me as an aspiring pianist. Learning with Lars&rsquo; finger strength exercises has been very beneficial. I&rsquo;ve noted in the pieces I play, that my 4th &amp; 5th finger on notes have shown great improvement! Thank you!&rdquo;
              </blockquote>
              <div className="sf8-students-say__card-person">
                <TestimonialAvatar initial="P" name="Patricia Cole" photoUrl={SENJA_PHOTOS.patricia} />
                <div>
                  <div className="sf8-students-say__card-name">Patricia Cole</div>
                  <div className="sf8-students-say__card-role">Piano Student</div>
                </div>
              </div>
            </div>
          </div>
          {reviewStrip ? (
            <div className="sf8-students-say__slider">
              {/* Senja sunset (2026-09-26): our own review strip replaces Senja
                  card marquee 375ae9ff — Pro Member, Super Fingers course and
                  Super Fingers book reviews from our testimonials database. */}
              {reviewStrip}
            </div>
          ) : null}
        </div>
      </section>

      {/* ════ §8 FAQ (FROZEN) ═════════════════════════════════════════════════ */}
      <section className="sf8-faq" aria-labelledby="faq-h2">
        <div className="sf8-faq__inner">
          <div className="eyebrow eyebrow--on-cream sf8-faq__eyebrow">{content.faqEyebrow}</div>
          <h2 id="faq-h2" className="sf8-faq__h2">{content.faqHeadline}</h2>
          <dl className="sf8-faq__list">
            {content.faqs.map((faq, i) => {
              const isOpen = openFaqIndex === i;
              return (
                <div key={i} className={`sf8-faq__item${isOpen ? ' sf8-faq__item--open' : ''}`}>
                  <dt>
                    <button type="button" className="sf8-faq__question" aria-expanded={isOpen} aria-controls={`faq-answer-${i}`} id={`faq-question-${i}`} onClick={() => setOpenFaqIndex(isOpen ? null : i)}>
                      <span className="sf8-faq__question-text">{faq.question}</span>
                      <span className={`sf8-faq__chevron${isOpen ? ' sf8-faq__chevron--open' : ''}`} aria-hidden="true"><ChevronDownIcon /></span>
                    </button>
                  </dt>
                  <dd id={`faq-answer-${i}`} role="region" aria-labelledby={`faq-question-${i}`} hidden={!isOpen} className="sf8-faq__answer">
                    <div className="sf8-faq__answer-inner">
                      {i === 0 ? (
                        <p>No — the 91-lesson video course (<em>Super Fingers Essential</em>) is <strong>not included with the ebook</strong>. The course covers all the exercises from the book and more, in <em>seven modules across 13+ hours of video lessons</em>. It is included with <a href="/pro-member">Pro Membership</a>, and Pro Membership is open at any time.</p>
                      ) : i === 1 ? (
                        <p>Yes. Every exercise carries a <strong>written explanation of the technique</strong>, and <em>the arm movements are drawn into the score</em> for clarity. The book is designed to stand on its own.</p>
                      ) : i === 2 ? (
                        <p>Some students finish the book in <strong>twelve months</strong>, others in twenty-four. <em>The variable is daily consistency and care with the slow phase — not talent.</em> Results depend on both the method and the work you put in: the more closely you follow the instructions, the better the result. Give it time.</p>
                      ) : i === 3 ? (
                        <p>The book is written for <strong>both beginners and advanced students</strong>. Beginners and advanced players both work with it because every exercise can be practised slowly or fast — <em>what changes is the tempo, the attention, and the level of detail you bring</em>. Wherever you start, you will finish the book a better pianist.</p>
                      ) : (
                        <p>Yes. The full plan is <strong>four volumes</strong> — Essential (the current book), Progressive, Advanced, and Transcendental. <em>Many exercises for Books 2 and 3 — and a few for Book 4 — are already drafted.</em> Lars wants to see how the first volume lands with students before shaping the follow-up, so each subsequent book reflects what the first one actually taught him about how students use the material.</p>
                      )}
                    </div>
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      </section>

      {/* ════ §9 FINAL CTA — Change 1 (cycle-9): v7 two-column layout restored ════
          Desktop ≥1024px: LEFT col = eyebrow + title + body + CTA; RIGHT col = book image.
          Mobile/tablet: single-col, image above text (order:-1 on image-col).
          DOM order: text-col (eyebrow→title→body→CTA) FIRST, image-col SECOND.
          (CSS `order:-1` on image-col places it above text visually on mobile.) */}
      <section className="sf8-final-cta" aria-labelledby="final-cta-h2">
        <div className="sf8-final-cta__inner">

          {/* Left column: eyebrow → title → body → CTA */}
          <div className="sf8-final-cta__text-col">
            <div className="eyebrow eyebrow--on-cream sf8-eyebrow-bracketed">
              {content.finalCtaEyebrow}
            </div>
            <h2 id="final-cta-h2" className="sf8-final-cta__h2">{content.finalCtaHeadline}</h2>
            <p className="sf8-final-cta__body">
              The book is a <strong>PDF, delivered instantly after purchase</strong>, and yours to <em>work with at your own pace</em>.
            </p>
            {/* Cycle-10 Change 3: arrow removed; push-up hover matches pricing button. */}
            <a href="#pricing" className="btn btn-primary sf8-final-cta__cta">
              Get the Book
            </a>
          </div>

          {/* Right column: angled 3D book cover image */}
          {/* Cycle-10 Change 1: book cover wrapped in LightboxTrigger */}
          <div className="sf8-final-cta__image-col">
            <LightboxTrigger
              src="/images/super-fingers/sf-book-lying.webp"
              alt="Super Fingers ebook cover, lying flat"
            >
              {/* zoom-only hover — position:relative + display:block on wrap */}
              <div className="sf8-book-img-wrap" style={{ cursor: 'zoom-in' }}>
                <Image
                  src="/images/super-fingers/sf-book-lying.webp"
                  alt="Super Fingers ebook cover, lying flat"
                  width={941} height={570}
                  loading="lazy" decoding="async"
                  className="sf8-book-img"
                  sizes="(max-width: 767px) 320px, (max-width: 1023px) 80vw, 50vw"
                />
              </div>
            </LightboxTrigger>
          </div>

        </div>
      </section>

      {/* Footer rendered by page.tsx — <Footer minimal={true} legalLinksClassName="pm11-legal-link" /> */}

      {/* One-time checkout pop-up cart — opens on "Get the Book" (2026-08-07). */}
      {checkoutCurrency && (
        <CheckoutModal
          mode="one-time"
          productName="Super Fingers e-book"
          coverImage="/images/store/super-fingers-book-square.webp"
          description="Sixty graded exercises for finger independence and arm technique. Book 1 by Lars Nelissen."
          currency={checkoutCurrency}
          onCurrencyChange={handleModalCurrencyChange}
          eur={{ priceId: 'sf_ebook_eur', unitAmount: 2600 }}
          usd={{ priceId: 'sf_ebook_usd', unitAmount: 2800 }}
          wholeAmounts
          // Keep the card a few minutes for the one-tap Pro offer on the
          // thank-you page (after-purchase Offer A, 2026-10-02).
          saveCardForOffer
          onClose={() => setCheckoutCurrency(null)}
        />
      )}

    </LightboxProvider>
  );
}
